import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NZ_MODAL_DATA } from 'ng-zorro-antd/modal';
import QRCode from 'qrcode';
import { ClipboardApiService } from '@app/shared/clipboard/clipboard-api.service';

export const TELEGRAM_BOT_URL = 'https://t.me/MedicalStaffSupport_bot';
export const MAX_BOT_URL = 'https://max.ru/id7713687558_bot';

export interface SupportCenterDialogData {
  webFormUrl?: string | null;
  portalUrl?: string | null;
  supportEmail?: string | null;
  maxMessengerEnabled?: boolean;
}

interface BotCard {
  readonly kind: 'bot';
  readonly iconSrc: string;
  readonly title: string;
  readonly caption: string;
  readonly url: string;
  readonly ctaLabel: string;
}

interface FormCard {
  readonly kind: 'form';
  readonly title: string;
  readonly caption: string;
  readonly url: string;
  readonly ctaLabel: string;
}

@Component({
  selector: 'rais-support-center-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NzButtonModule, NzIconModule],
  template: `
    <div class="support-center">
      <div class="cards-row" [class.cards-row--single]="botCards.length === 1">
        @for (card of botCards; track card.url) {
          <div class="card card--with-qr">
            <div class="card__body">
              <div class="card__header">
                <img class="card__brand-icon" [src]="card.iconSrc" alt="" />
                <div class="card__text">
                  <div class="card__title">{{ card.title }}</div>
                  <div class="card__caption card__caption--brand">{{ card.caption }}</div>
                </div>
              </div>
              <a nz-button class="card__cta" [href]="card.url" target="_blank" rel="noopener">
                {{ card.ctaLabel }}
                <span nz-icon nzType="export" nzTheme="outline"></span>
              </a>
            </div>
            @if (qrDataUrls()[card.url]) {
              <img class="card__qr" [src]="qrDataUrls()[card.url]" alt="QR" />
            }
          </div>
        }
      </div>

      @if (supportEmail) {
        <div class="card card--email" data-testid="support-center-email-card">
          <div class="card__email-row">
            <span class="card__email-icon" nz-icon nzType="mail" nzTheme="outline"></span>
            <div class="card__text">
              <div class="card__title">Email</div>
              <div class="card__caption card__caption--brand">{{ supportEmail }}</div>
            </div>
          </div>
          <button nz-button class="card__cta" (click)="copyEmail()">
            Скопировать
            <span nz-icon nzType="copy" nzTheme="outline"></span>
          </button>
        </div>
      }

      @if (formCards().length > 0) {
        <div class="cards-row" [class.cards-row--single]="formCards().length === 1">
          @for (card of formCards(); track card.url) {
            <div class="card card--with-qr">
              <div class="card__body">
                <div class="card__header">
                  <div class="card__text">
                    <div class="card__title">{{ card.title }}</div>
                    <div class="card__caption card__caption--secondary">{{ card.caption }}</div>
                  </div>
                </div>
                <a nz-button class="card__cta" [href]="card.url" target="_blank" rel="noopener">
                  {{ card.ctaLabel }}
                  <span nz-icon nzType="export" nzTheme="outline"></span>
                </a>
              </div>
              @if (qrDataUrls()[card.url]) {
                <img class="card__qr" [src]="qrDataUrls()[card.url]" alt="QR" />
              }
            </div>
          }
        </div>
      }

      <div class="support-center__footer">Не нашли ответ? Напишите нам удобным Вам способом, и мы поможем!</div>
    </div>
  `,
  styles: `
    :host {
      display: block;
    }

    .support-center {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .cards-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }

    .cards-row--single {
      grid-template-columns: 1fr;
    }

    .card {
      background: #ffffff;
      border: 1px solid #e4e4e4;
      border-radius: 16px;
      padding: 16px;
      display: flex;
      gap: 16px;
      align-items: flex-start;
    }

    .card--with-qr {
      justify-content: space-between;
    }

    .card__body {
      flex: 1 1 0;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 16px;
      align-items: flex-start;
    }

    .card__header {
      display: flex;
      align-items: center;
      gap: 16px;
      width: 100%;
    }

    .card__brand-icon {
      flex-shrink: 0;
      width: 40px;
      height: 40px;
      display: block;
    }

    .card__email-icon {
      flex-shrink: 0;
      font-size: 40px;
      line-height: 40px;
      color: #00847a;
    }

    .card__text {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;
      flex: 1 1 auto;
    }

    .card__title {
      font-family: Inter, sans-serif;
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: -0.28px;
      color: #000000;
      word-break: break-word;
    }

    .card__caption {
      font-family: Inter, sans-serif;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: -0.28px;
      word-break: break-word;
    }

    .card__caption--brand {
      color: #00847a;
    }

    .card__caption--secondary {
      color: #515151;
    }

    .card__cta {
      background: #ccedeb !important;
      color: #515151 !important;
      border: none !important;
      border-radius: 4px !important;
      padding: 0 16px !important;
      height: 32px !important;
      display: inline-flex !important;
      align-items: center;
      gap: 8px;
      font-family: Inter, sans-serif;
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: -0.28px;
    }

    .card__cta:hover {
      background: #b8e5e2 !important;
    }

    .card__qr {
      flex-shrink: 0;
      width: 94px;
      height: 92px;
      border-radius: 8px;
      object-fit: contain;
      background: #ffffff;
    }

    .card--email {
      align-items: center;
      justify-content: space-between;
    }

    .card__email-row {
      display: flex;
      align-items: center;
      gap: 16px;
      flex: 1 1 0;
      min-width: 0;
    }

    .support-center__footer {
      font-family: Inter, sans-serif;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: -0.28px;
      color: #515151;
      margin-top: 8px;
    }
  `,
})
export class SupportCenterDialogComponent {
  private clipboardApiService = inject(ClipboardApiService);
  readonly data = inject<SupportCenterDialogData>(NZ_MODAL_DATA);

  protected readonly supportEmail = this.data.supportEmail ?? null;

  protected readonly botCards: readonly BotCard[] = [
    {
      kind: 'bot',
      iconSrc: 'assets/svg/support-center/telegram-brand.svg',
      title: 'Telegram',
      caption: '@MedicalStaffSupport_bot',
      url: TELEGRAM_BOT_URL,
      ctaLabel: 'Открыть бота',
    },
    ...(this.data.maxMessengerEnabled
      ? [
          {
            kind: 'bot' as const,
            iconSrc: 'assets/svg/support-center/max-brand.svg',
            title: 'MAX',
            caption: 'max.ru/id7713687558_bot',
            url: MAX_BOT_URL,
            ctaLabel: 'Открыть бота',
          },
        ]
      : []),
  ];

  protected readonly formCards = signal<FormCard[]>([]);

  protected readonly qrDataUrls = signal<Record<string, string>>({});

  constructor() {
    const cards: FormCard[] = [];
    if (this.data.webFormUrl) {
      cards.push({
        kind: 'form',
        title: 'Веб-форма обращения',
        caption: 'Опишите вашу ситуацию через веб-форму',
        url: this.data.webFormUrl,
        ctaLabel: 'Открыть форму',
      });
    }
    if (this.data.portalUrl) {
      cards.push({
        kind: 'form',
        title: 'Портал поддержки',
        caption: 'База знаний, статус обращений и новости.',
        url: this.data.portalUrl,
        ctaLabel: 'Открыть портал',
      });
    }
    this.formCards.set(cards);

    effect(() => {
      const urls = [...this.botCards.map((c) => c.url), ...this.formCards().map((c) => c.url)];
      void this.generateQrs(urls);
    });
  }

  private async generateQrs(urls: string[]): Promise<void> {
    const generated: Record<string, string> = {};
    for (const url of urls) {
      try {
        generated[url] = await QRCode.toDataURL(url, { margin: 1, width: 188 });
      } catch {
        generated[url] = '';
      }
    }
    this.qrDataUrls.set(generated);
  }

  protected copyEmail(): void {
    void this.clipboardApiService.writeText(this.supportEmail);
  }
}
